import { motion } from 'framer-motion';
import {
  Plus, Settings, Timer, Calendar as CalendarIcon, Search,
  LayoutGrid, List as ListIcon, Grid3X3, BarChart3, Filter, Menu, X,
  SlidersHorizontal,
} from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useAuroraTheme } from '@/components/aurora/useAuroraTheme';
import { aurora } from '@/components/aurora/tokens';

export type ViewKey = 'kanban' | 'table' | 'eisenhower' | 'kpi';

export interface PriorityOption {
  value: string;
  label: string;
  emoji: string;
}

export interface UserOption {
  id: string;
  first_name?: string;
  last_name?: string;
}

export interface BoardLite {
  id: string;
  name: string;
  color?: string;
  board_type?: string;
  workgroup_name?: string;
  task_count?: number;
}

interface Props {
  board: BoardLite | null;
  taskCount: number;
  isAdmin: boolean;
  activeView: ViewKey;
  onChangeView: (view: ViewKey) => void;
  searchQuery: string;
  onChangeSearch: (q: string) => void;
  filterPriority: string;
  onChangePriority: (v: string) => void;
  filterAssignee: string;
  onChangeAssignee: (v: string) => void;
  priorities: PriorityOption[];
  users: UserOption[];
  activeTimerLabel?: string | null;
  onCreateTask: () => void;
  onOpenBoardSettings: () => void;
  onOpenTaskSettings: () => void;
  onOpenCalendar: () => void;
  onOpenMobileSidebar: () => void;
  onOpenMobileFilters: () => void;
  isMobile: boolean;
}

const VIEWS: { id: ViewKey; icon: React.ComponentType<{ className?: string }>; label: string }[] = [
  { id: 'kanban', icon: LayoutGrid, label: 'Kanban' },
  { id: 'table', icon: ListIcon, label: 'Tabella' },
  { id: 'eisenhower', icon: Grid3X3, label: 'Eisenhower' },
  { id: 'kpi', icon: BarChart3, label: 'KPI' },
];

function badgeForType(type?: string, workgroupName?: string) {
  if (type === 'personal') return 'Personale';
  if (type === 'team') return workgroupName || 'Team';
  if (type === 'project') return 'Project';
  return null;
}

export function TasksAuroraHeader(props: Props) {
  const {
    board, taskCount, isAdmin, activeView, onChangeView,
    searchQuery, onChangeSearch, filterPriority, onChangePriority,
    filterAssignee, onChangeAssignee, priorities, users,
    activeTimerLabel, onCreateTask, onOpenBoardSettings, onOpenTaskSettings,
    onOpenCalendar, onOpenMobileSidebar, onOpenMobileFilters, isMobile,
  } = props;
  const { t } = useAuroraTheme();

  const color = board?.color || t.primary;
  const eyebrow = badgeForType(board?.board_type, board?.workgroup_name);

  const activeFiltersCount = useMemo(() => {
    let c = 0;
    if (filterPriority && filterPriority !== 'all') c++;
    if (filterAssignee && filterAssignee !== 'all') c++;
    if (searchQuery && searchQuery.trim()) c++;
    return c;
  }, [filterPriority, filterAssignee, searchQuery]);

  return (
    <motion.header
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: aurora.ease }}
      className="shrink-0 px-3 md:px-5 pt-3 md:pt-4 pb-3"
      style={{
        background: t.panel,
        borderBottom: `1px solid ${t.border}`,
        backdropFilter: 'blur(12px)',
      }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {isMobile && (
            <button
              onClick={onOpenMobileSidebar}
              aria-label="Apri sidebar board"
              className="md:hidden w-9 h-9 grid place-items-center rounded-md shrink-0"
              style={{ background: t.panel2, color: t.text, border: `1px solid ${t.border}` }}
            >
              <Menu className="w-4 h-4" />
            </button>
          )}
          {(() => {
            const Avatar = (
              <div
                className="w-10 h-10 rounded-xl shrink-0 grid place-items-center"
                style={{
                  background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
                  boxShadow: `0 8px 24px -10px ${color}66`,
                }}
                aria-hidden
              >
                <span className="text-white font-semibold text-[15px]">{(board?.name || 'B').charAt(0).toUpperCase()}</span>
              </div>
            );
            const Title = (
              <h1
                className="truncate"
                style={{
                  fontFamily: aurora.sans,
                  fontWeight: 600,
                  fontSize: isMobile ? 18 : 20,
                  lineHeight: 1.15,
                  letterSpacing: '-0.01em',
                  color: t.text,
                }}
              >
                {board?.name || 'Task Board'}
              </h1>
            );
            return board ? (
              <button
                type="button"
                onClick={onOpenBoardSettings}
                className="flex items-center gap-3 min-w-0 rounded-xl text-left transition-opacity hover:opacity-80"
                aria-label="Impostazioni board"
                title="Impostazioni board"
              >
                {Avatar}
                {Title}
              </button>
            ) : (
              <>
                {Avatar}
                {Title}
              </>
            );
          })()}
          <div className="flex items-center gap-2.5 flex-wrap min-w-0 flex-1">
            {eyebrow && (
              <span
                className="text-[10px] uppercase tracking-[0.14em] font-semibold px-2 py-0.5 rounded shrink-0"
                style={{
                  background: `${color}1A`,
                  color,
                  border: `1px solid ${color}33`,
                }}
              >
                {eyebrow}
              </span>
            )}
            <span className="text-[11px] shrink-0" style={{ color: t.muted }}>
              {taskCount} task
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
          {activeTimerLabel && (
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-mono"
              style={{ background: `${t.primary}18`, color: t.primary, border: `1px solid ${t.primary}33` }}
              aria-live="polite"
            >
              <Timer className="w-3 h-3" />
              {activeTimerLabel}
            </motion.span>
          )}
          {!isMobile && (
            <>
              <button
                onClick={onOpenCalendar}
                className="hidden md:inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-[12px] font-medium transition-colors"
                style={{ background: t.panel2, color: t.muted, border: `1px solid ${t.border}` }}
                onMouseEnter={(e) => { e.currentTarget.style.color = t.text; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = t.muted; }}
                aria-label="Vedi nel calendario"
              >
                <CalendarIcon className="w-3.5 h-3.5" />
                Calendario
              </button>
              {isAdmin && (
                <button
                  onClick={onOpenTaskSettings}
                  className="inline-flex items-center justify-center h-8 w-8 rounded-md transition-colors"
                  style={{ background: t.panel2, color: t.muted, border: `1px solid ${t.border}` }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = t.text; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = t.muted; }}
                  aria-label="Impostazioni Task"
                  title="Impostazioni Task"
                >
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </>
          )}
          {!isMobile && (
            <motion.button
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={onCreateTask}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-semibold"
              style={{
                background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primaryStrong} 100%)`,
                color: '#fff',
                boxShadow: `0 6px 18px -6px ${t.primary}88`,
              }}
              aria-label="Nuovo task"
            >
              <Plus className="w-4 h-4" />
              Nuovo Task
            </motion.button>
          )}
          {isMobile && (
            <button
              onClick={onOpenCalendar}
              className="inline-flex items-center justify-center h-9 w-9 rounded-md"
              style={{ background: t.panel2, color: t.muted, border: `1px solid ${t.border}` }}
              aria-label="Vedi nel calendario"
              title="Calendario"
            >
              <CalendarIcon className="w-4 h-4" />
            </button>
          )}
          {isMobile && isAdmin && (
            <button
              onClick={onOpenTaskSettings}
              className="inline-flex items-center justify-center h-9 w-9 rounded-md"
              style={{ background: t.panel2, color: t.muted, border: `1px solid ${t.border}` }}
              aria-label="Impostazioni task"
              title="Impostazioni task"
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3">
        <ViewSegmented activeView={activeView} onChangeView={onChangeView} />

        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="relative flex-1 md:max-w-[260px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: t.faint }} />
            <input
              value={searchQuery}
              onChange={(e) => onChangeSearch(e.target.value)}
              placeholder="Cerca task…"
              aria-label="Cerca task"
              className="w-full h-9 pl-8 pr-2 rounded-lg text-[12.5px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500 transition-colors"
              style={{
                background: t.panel2,
                border: `1px solid ${t.border}`,
                color: t.text,
              }}
            />
          </div>

          {!isMobile ? (
            <>
              <NativeSelect
                value={filterPriority || 'all'}
                onChange={onChangePriority}
                ariaLabel="Filtra per priorità"
                placeholder="Priorità"
                options={[{ value: 'all', label: 'Tutte le priorità' }, ...priorities.map(p => ({ value: p.value, label: `${p.emoji} ${p.label}` }))]}
              />
              <NativeSelect
                value={filterAssignee || 'all'}
                onChange={onChangeAssignee}
                ariaLabel="Filtra per assegnatario"
                placeholder="Assegnatario"
                options={[{ value: 'all', label: 'Tutti gli assegnatari' }, ...users.map(u => ({ value: u.id, label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id }))]}
              />
            </>
          ) : (
            <button
              onClick={onOpenMobileFilters}
              className="relative h-9 px-3 inline-flex items-center gap-1.5 rounded-lg text-[12.5px] font-medium"
              style={{
                background: t.panel2, color: t.text, border: `1px solid ${t.border}`,
              }}
              aria-label={`Filtri (${activeFiltersCount} attivi)`}
            >
              <Filter className="w-3.5 h-3.5" />
              Filtri
              {activeFiltersCount > 0 && (
                <span
                  className="ml-1 inline-grid place-items-center text-[10px] font-bold rounded-full"
                  style={{
                    background: t.primary, color: '#fff', minWidth: 18, height: 18,
                    padding: '0 5px',
                  }}
                >
                  {activeFiltersCount}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </motion.header>
  );
}

function ViewSegmented({ activeView, onChangeView }: { activeView: ViewKey; onChangeView: (v: ViewKey) => void }) {
  const { t } = useAuroraTheme();
  return (
    <div
      role="tablist"
      aria-label="Vista board"
      className="inline-flex items-center gap-0.5 p-1 rounded-lg overflow-x-auto"
      style={{ background: t.panel2, border: `1px solid ${t.border}` }}
    >
      {VIEWS.map((v) => {
        const isActive = activeView === v.id;
        return (
          <button
            key={v.id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChangeView(v.id)}
            className="relative inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md text-[12px] font-medium whitespace-nowrap transition-colors"
            style={{
              color: isActive ? t.text : t.muted,
              background: isActive ? t.bg : 'transparent',
              boxShadow: isActive ? `0 1px 2px ${t.bg}55, 0 0 0 1px ${t.border}` : 'none',
            }}
          >
            <v.icon className="w-3.5 h-3.5" />
            {v.label}
          </button>
        );
      })}
    </div>
  );
}

function NativeSelect({
  value, onChange, options, ariaLabel, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  ariaLabel?: string;
  placeholder?: string;
}) {
  const { t } = useAuroraTheme();
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={ariaLabel}
      className="h-9 px-2.5 rounded-lg text-[12.5px] font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500 transition-colors min-w-[120px] max-w-[180px] truncate"
      style={{
        background: t.panel2,
        color: t.text,
        border: `1px solid ${t.border}`,
      }}
    >
      {placeholder && <option value="all" disabled hidden>{placeholder}</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value} style={{ background: t.bg, color: t.text }}>{o.label}</option>
      ))}
    </select>
  );
}

interface MobileFiltersProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterPriority: string;
  onChangePriority: (v: string) => void;
  filterAssignee: string;
  onChangeAssignee: (v: string) => void;
  priorities: PriorityOption[];
  users: UserOption[];
  onClearAll: () => void;
}

export function MobileFiltersSheet({
  open, onOpenChange, filterPriority, onChangePriority, filterAssignee, onChangeAssignee,
  priorities, users, onClearAll,
}: MobileFiltersProps) {
  const { t } = useAuroraTheme();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onOpenChange(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  if (!open) return null;
  return (
    <>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="md:hidden fixed inset-0 z-40"
        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(2px)' }}
        onClick={() => onOpenChange(false)}
        aria-hidden
      />
      <motion.div
        role="dialog"
        aria-label="Filtri task"
        initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
        transition={{ type: 'spring', stiffness: 320, damping: 32 }}
        className="md:hidden fixed inset-x-0 bottom-0 z-50 rounded-t-2xl"
        style={{
          background: t.panel,
          borderTop: `1px solid ${t.border}`,
          color: t.text,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${t.border}` }}>
          <span className="text-[13px] font-semibold" style={{ color: t.text }}>Filtri</span>
          <div className="flex items-center gap-1">
            <button
              onClick={onClearAll}
              className="text-[11.5px] px-2 py-1 rounded-md"
              style={{ color: t.muted }}
            >
              Reset
            </button>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Chiudi filtri"
              className="w-8 h-8 grid place-items-center rounded-md"
              style={{ color: t.muted }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: t.muted }}>Priorità</label>
            <select
              value={filterPriority || 'all'}
              onChange={(e) => onChangePriority(e.target.value)}
              className="w-full mt-1.5 h-10 px-3 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500"
              style={{ background: t.panel2, color: t.text, border: `1px solid ${t.border}` }}
            >
              <option value="all">Tutte le priorità</option>
              {priorities.map((p) => (
                <option key={p.value} value={p.value}>{p.emoji} {p.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color: t.muted }}>Assegnatario</label>
            <select
              value={filterAssignee || 'all'}
              onChange={(e) => onChangeAssignee(e.target.value)}
              className="w-full mt-1.5 h-10 px-3 rounded-lg text-[13px] focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-600 dark:focus:ring-blue-500"
              style={{ background: t.panel2, color: t.text, border: `1px solid ${t.border}` }}
            >
              <option value="all">Tutti</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>{`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="w-full h-10 rounded-lg text-[13px] font-semibold"
            style={{ background: t.primary, color: '#fff' }}
          >
            Applica filtri
          </button>
        </div>
      </motion.div>
    </>
  );
}

interface FabProps {
  onClick: () => void;
}

export function MobileNewTaskFab({ onClick }: FabProps) {
  const { t } = useAuroraTheme();
  return (
    <motion.button
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      whileTap={{ scale: 0.94 }}
      onClick={onClick}
      aria-label="Nuovo task"
      className="md:hidden fixed z-30 right-4 bottom-4 w-14 h-14 rounded-full grid place-items-center"
      style={{
        background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primaryStrong} 100%)`,
        color: '#fff',
        boxShadow: `0 14px 30px -10px ${t.primary}99, 0 4px 12px -4px ${t.primary}55`,
      }}
    >
      <Plus className="w-6 h-6" />
    </motion.button>
  );
}

export default TasksAuroraHeader;
